import React, { FC, useState, useContext, useEffect } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery, useMutation, useLazyQuery } from '@apollo/react-hooks';

import { getRedirectURL } from 'Utils/index';
import { useRouter, useHistory } from 'Utils/router';
import Nav from 'Components/Headers/Nav';
import UserDetailsContext from 'Context/userDetailsContext';
import { useDispatch, useSelector } from 'react-redux';
import { resetLeadsList } from 'Containers/AudienceExplore/actions/individuallist.actions';
import withErrorBoundary from 'HOC/ErrorBoundary';
import goodDataGql from 'Containers/GoodData/dashboards/YourEDUVSNetwork/gqlQuery';
import generateLinks from './links';
import {
  getInstAccessModules,
  instNameGql,
  getInstNameAndUniversitiesGql,
  changeUniversityGql,
} from './gqlQuery';

interface Props {
  setModuleAccess: any;
  setIsSuperUser: any;
}

const NavContainer: FC<Props> = (props: Props) => {
  const { setModuleAccess, setIsSuperUser } = props;
  const [gdGQLData, setGDGQLData] = useState(undefined);
  const userDetails = useContext(UserDetailsContext);
  const { userId, instId } = userDetails;
  const [variables] = useState({ instId, userId });
  const dispatch = useDispatch();
  const { fullAccess } = useSelector((store: any) => store);
  const history = useHistory();

  // Default object to render the Profile Component
  const [profileData, setProfileData] = useState({
    instName: '',
    univList: [],
    showAriaOptimize: false,
  });
  const isSuperUser =
    userDetails.accountType.toLowerCase() === 'super' ||
    userDetails.accountType.toLowerCase() === 'superadmin';
  setIsSuperUser(isSuperUser);
  const { error: instError } = useQuery(
    isSuperUser ? getInstNameAndUniversitiesGql : instNameGql,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        userId,
        instId,
      },
      onCompleted: (response: any) => {
        const {
          institutions: { name = '' },
          user: {
            organizations = [], //eslint-disable-line
          } = { organizations: [] },
        } = response;
        setProfileData({
          instName: name,
          univList: organizations,
          showAriaOptimize: profileData.showAriaOptimize,
        });
      },
      fetchPolicy: 'cache-and-network',
    }
  );

  // Check which modules inst has access to //
  const [runModulesQuery, { data: instModuleData }] = useLazyQuery(
    getInstAccessModules,
    {
      variables,
      onCompleted: (response) => {
        setModuleAccess(
          generateLinks(response.institutions.modules, gdGQLData, fullAccess)
            .modulesModel
        );
        let showAriaOptimize = false;
        response.institutions.modules.forEach((module: any) => {
          if (module.module === 'ARIA_OPTIMIZE' && module.enabled)
            showAriaOptimize = true;
        });
        if (showAriaOptimize) {
          setProfileData({
            ...profileData,
            showAriaOptimize,
          });
        }
      },
    }
  );

  // GraphQL User.GoodDataClaims Query
  useQuery(goodDataGql, {
    variables: {
      id: userDetails.userId,
      accountId: userDetails.accountId,
      base64Encoded: true,
      instId: userDetails.instId,
    },
    onCompleted: (response) => {
      setGDGQLData(response);
      runModulesQuery();
    },
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.log(err);
      setGDGQLData([]);
      runModulesQuery();
    },
  });

  if (instError) {
    const err = instError.message.search('status code 403');
    if (err > 0) window.location.replace(getRedirectURL());
  }

  const router = useRouter();
  // useRouter().pathname gets the current path route//
  const active = router.pathname;
  const inquiries = (pathName: string) => {
    /*
        If the pathName is /inquiries/explore or inquiries/details
          Delete the Lists Lists
          
        Else If the pathName is /inquiries/lists
          Delete the Expore Lists
      */
    const isItAListsRoute = pathName.startsWith('/inquiries/lists');

    if (isItAListsRoute) {
      dispatch(resetLeadsList({ viewType: 'Explore' }));
    }
    // Check to see if the route is the details route.  if so, don't delete
    else if (pathName.startsWith('/inquiries/explore'))
      dispatch(resetLeadsList({ viewType: 'Lists' }));
  };

  useEffect(() => {
    // Listen for a change in routes.
    // if (active !== "/") {
    // Let's see if it's a route that is not part of inquires
    const doesStartWith = active.startsWith('/inquiries');
    switch (doesStartWith) {
      case true: // Check if the user is coming from a child route.
        inquiries(active);
        break;
      default:
        // If it's not an inquiries route then clear out the checked checkboxes list
        dispatch(resetLeadsList({ viewType: 'All' }));
        break;
    }
    // }
  }, [active]);

  const [changeUniversity] = useMutation(changeUniversityGql, {
    onCompleted: (response) => {
      if (response.loginAs && response.loginAs.success) {
        if (fullAccess) {
          history.push('?fullAccess=1');
        }
        window.location.reload();
      }
    },
  });

  // This is placed because React needs to render the same amount of hooks on each render.
  if (instError && instError.message.search('status code 403') > 0)
    window.location.replace(getRedirectURL());
  if (instModuleData && gdGQLData)
    return (
      <Nav
        active={active}
        navList={
          generateLinks(
            instModuleData.institutions.modules,
            gdGQLData,
            fullAccess
          ).links
        }
        firstName={`${userDetails.firstname}`}
        lastName={`${userDetails.lastname}`}
        email={`${userDetails.email}`}
        profileData={profileData}
        changeUniversity={changeUniversity}
      />
    );
  return null;
};

export default withErrorBoundary(NavContainer);
